import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../api/services';
import { formatDate } from '../utils/helpers';
import './NotificationBell.css';

// 業務邏輯：依通知類型組出對應的中文描述文字，actor 為觸發者顯示名稱。
const describe = (n) => {
  const actor = n.actor_name || '有人';
  switch (n.type) {
    case 'COMMENT': return `${actor} 回應了你的文章`;
    case 'REPLY': return `${actor} 回覆了你的留言`;
    case 'ACCEPTED': return `${actor} 採納你的回答為最佳解答`;
    case 'MENTION': return `${actor} 在內容中提到了你`;
    case 'POST_VOTE': return `${actor} 對你的文章按讚`;
    case 'SUBSCRIPTION': return `你追蹤的領域有新文章`;
    case 'BOUNTY_CLAIMED': return `${actor} 領取了「${n.bounty_title || '懸賞案件'}」`;
    case 'BOUNTY_SUBMITTED': return `${actor} 在「${n.bounty_title || '懸賞案件'}」提出結案`;
    case 'BADGE_AWARDED': return `${actor} 頒給你「${n.content || '榮譽徽章'}」徽章`;
    case 'REWARD_APPROVED': return `${actor} 同意了你兌換「${n.content || '商品'}」`;
    case 'REWARD_REJECTED': return `${actor} 婉拒了你兌換「${n.content || '商品'}」`;
    default: return `${actor} 有新動態`;
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  // 輪詢未讀數量，讓鈴鐺紅點即時反映新通知
  const refreshUnread = useCallback(async () => {
    try {
      const res = await notificationService.unreadCount();
      setUnread(res.data?.count || 0);
    } catch { /* 靜默失敗，不干擾主流程 */ }
  }, []);

  useEffect(() => {
    refreshUnread();
    const timer = setInterval(refreshUnread, 60000);
    return () => clearInterval(timer);
  }, [refreshUnread]);

  // 點擊元件外部時關閉下拉
  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const res = await notificationService.list();
        setItems(res.data || []);
      } catch { /* 靜默失敗 */ } finally {
        setLoading(false);
      }
    }
  };

  const handleItemClick = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await notificationService.markRead(n.id); } catch { /* 略過 */ }
      refreshUnread();
    }
    // 懸賞結案通知優先導向懸賞詳情頁；兌換通知導向兌換商城；一般通知導向文章頁
    if (n.bounty_id) navigate(`/bounties/${n.bounty_id}`);
    else if (n.type === 'REWARD_APPROVED' || n.type === 'REWARD_REJECTED') navigate('/rewards');
    else if (n.post_id) navigate(`/posts/${n.post_id}`);
  };

  const handleMarkAll = async () => {
    try {
      await notificationService.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnread(0);
    } catch { /* 略過 */ }
  };

  return (
    <div className="notif-bell" ref={wrapRef}>
      <button className="notif-trigger" onClick={toggleOpen} title="通知" aria-label="通知">
        <span className="notif-icon">🔔</span>
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-head">
            <span className="notif-title">通知</span>
            {unread > 0 && (
              <button className="notif-mark-all" onClick={handleMarkAll}>全部已讀</button>
            )}
          </div>
          <div className="notif-list">
            {loading && <div className="notif-empty">載入中...</div>}
            {!loading && items.length === 0 && <div className="notif-empty">目前沒有通知</div>}
            {!loading && items.map((n) => (
              <button
                key={n.id}
                className={`notif-item ${n.is_read ? '' : 'unread'}`}
                onClick={() => handleItemClick(n)}
              >
                <div className="notif-item-text">{describe(n)}</div>
                {n.post_title && <div className="notif-item-post">「{n.post_title}」</div>}
                {!n.post_title && n.bounty_title && <div className="notif-item-post">「{n.bounty_title}」</div>}
                <div className="notif-item-time">{formatDate(n.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
